// components/teams/TeamBody.tsx
"use client";

import { Link } from '@/i18n/navigation';
import { Home } from 'lucide-react';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';

interface LayerConfig {
  label: string; bg: string; text: string;
  border: string; stripe: string; fill: string;
}

interface Team {
  slug: string;
  domain: string;
  layer: number;
  isEnabled: boolean;
  isB2bOnly: boolean;
  minPriceMicroUsd: number;
  maxPriceMicroUsd: number;
  minSeconds: number;
  maxSeconds: number;
  requireCountryIn: string[];
  requireCompliance: string[];
  requireNoTraining: boolean;
  requireZdr: boolean;
  runBudgetTokens: number | null;
}

interface PeerTeam {
   slug: string;
   layer: number;
   domain: string; 
  }

interface Props {
  team: Team;
  name: string;
  domain: string;
  howItWorks: string | null;
  capabilities: string[];
  lc: LayerConfig;
  ui: Record<string, string>;
  peers: PeerTeam[];
  locale: string;
}

export default function TeamBody({ team, name, domain, howItWorks, capabilities, lc, ui, peers, locale  }: Props) {

  const fmtPrice = (u: number) => {
    const usd = u / 1_000_000;
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    if (usd < 1)    return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
  };

  const priceStr = team.minPriceMicroUsd === team.maxPriceMicroUsd
    ? fmtPrice(team.minPriceMicroUsd)
    : `${fmtPrice(team.minPriceMicroUsd)} — ${fmtPrice(team.maxPriceMicroUsd)}`;

  const fmtTime = (s: number) => s < 60 ? `${s}s` : `${Math.round(s / 60)}min`;
  const timeStr = team.minSeconds === team.maxSeconds
    ? fmtTime(team.minSeconds)
    : `${fmtTime(team.minSeconds)} — ${fmtTime(team.maxSeconds)}`;

  const contextStr = team.runBudgetTokens
    ? `Up to ${(team.runBudgetTokens * 4).toLocaleString()} characters`
    : ui.unlimited;

  return (
    <div className="max-w-5xl mx-auto px-6 pt-6 pb-12">

      
      {/* Breadcrumb */}
              <div className="mb-8">
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild><Link href="/"><Home className="w-4 h-4" /></Link></BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild><Link href="/pricing">{ui.teamsNav}</Link></BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage className="capitalize">{domain}</BreadcrumbPage>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{name}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
      <div className="flex flex-col lg:flex-row gap-8 items-start">

        {/* Левый блок — белый, описание + capabilities */}
        <div className="flex-1 flex flex-col gap-8">

          {/* How it works */}
          {howItWorks && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-3">
                {ui.howItWorks}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                {howItWorks}
              </p>
            </div>
          )}

          {/* Capabilities */}
          {capabilities.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                {ui.whatItDoes}
              </h2>
              <ul className="flex flex-col gap-3">
                {capabilities.map((cap, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs ${lc.stripe}`}>
                      ✓
                    </span>
                    <span className="text-base text-slate-700">{cap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Правый блок — светло-серый прямоугольник с тенью */}
        <div className="w-full lg:w-80 flex-shrink-0 bg-slate-50 rounded-2xl shadow-md p-6 flex flex-col gap-6">

          {/* Цена */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
              {ui.priceTime}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-slate-900">{priceStr}</span>
              <span className="text-sm text-slate-400">{ui.perRequest}</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-sm text-slate-500">{timeStr}</span>
              <span className="text-xs text-slate-400">· {ui.approximate}</span>
            </div>
          </div>

          <div className="h-px bg-slate-200" />

          {/* Privacy */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              {ui.privacy}
            </p>
            <div className="flex flex-col gap-3">

              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-slate-500">{ui.processingRegion}</span>
                <span className="text-xs font-semibold text-slate-800 text-right">
                  {team.requireCountryIn.length > 0
                    ? team.requireCountryIn.join(', ')
                    : ui.global}
                </span>
              </div>

              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-slate-500">{ui.compliance}</span>
                <span className="text-xs font-semibold text-slate-800 text-right">
                  {team.requireCompliance.length > 0
                    ? team.requireCompliance.join(', ')
                    : ui.standard}
                </span>
              </div>

              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-slate-500">{ui.noTraining}</span>
                <span className={`text-xs font-semibold ${team.requireNoTraining ? 'text-green-600' : 'text-slate-500'}`}>
                  {team.requireNoTraining ? ui.never : ui.mayBeUsed}
                </span>
              </div>

              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-slate-500">{ui.zdr}</span>
                <span className={`text-xs font-semibold ${team.requireZdr ? 'text-green-600' : 'text-slate-500'}`}>
                  {team.requireZdr ? ui.enabled : ui.standardRetention}
                </span>
              </div>

              <div className="flex items-start justify-between gap-2">
                <span className="text-xs text-slate-500">{ui.context}</span>
                <span className="text-xs font-semibold text-slate-800 text-right">
                  {contextStr}
                </span>
              </div>

            </div>
          </div>

          <div className="h-px bg-slate-200" />

          {/* Footer CTA */}
          <div className="text-center">
            <p className="text-xs text-slate-400 mb-3">{ui.readyToTry} {name}?</p>
            {team.isB2bOnly ? (
              <a
                href="mailto:hello@layvers.com"
                className="w-full inline-block text-center px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors"
              >
                {ui.contactUs}
              </a>
            ) : team.isEnabled ? (
              <a
                href={`/chat?team=${team.slug}`}
                className="w-full inline-block text-center px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
              >
                {ui.tryNow}
              </a>
            ) : (
              <span className="w-full inline-block text-center px-4 py-2.5 rounded-xl bg-slate-100 text-slate-400 text-sm font-semibold cursor-not-allowed">
                {ui.comingSoon}
              </span>
            )}
          </div>

        </div>
      </div>
      {peers.length > 1 && (() => {
          const idx = peers.findIndex(p => p.slug === team.slug);
          return (
            <div className="flex items-center gap-2 mt-8 flex-wrap">
              <a href={idx > 0 ? `/${locale}/teams/${peers[idx-1].slug}` : '#'}
                className={`p-2 rounded-lg border ${idx > 0 ? 'border-slate-200 hover:bg-slate-50' : 'border-slate-100 text-slate-300 pointer-events-none'}`}>
                ‹
              </a>
              {peers.map((p, i) => (
                <a key={p.slug} href={`/${locale}/teams/${p.slug}`}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold border transition-colors
                    ${p.slug === team.slug ? `${lc.bg} ${lc.text} ${lc.border}` : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  {i+1}
                </a>
              ))}
              <a href={idx < peers.length-1 ? `/${locale}/teams/${peers[idx+1].slug}` : '#'}
                className={`p-2 rounded-lg border ${idx < peers.length-1 ? 'border-slate-200 hover:bg-slate-50' : 'border-slate-100 text-slate-300 pointer-events-none'}`}>
                ›
              </a>
              <a href={`/${locale}/pricing`}
                className="ml-2 text-xs font-semibold text-slate-400 hover:text-slate-600 uppercase tracking-widest">
                View All
              </a>
            </div>
          );
        })()}
    </div>
  );
}